import React, {
  FC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { defineMessages, useIntl } from "react-intl";
import {
  ErrorProvider,
  IntlContext,
  PreBillContext,
  UppAuthContext,
} from "@app/provider";
import { useErrorController } from "@natera/platform/lib/hooks";
import DateOfBirthVerification from "@app/preBill/components/dateOfBirthVerification";
import { Link } from "@natera/navigation";
import acknowledgements from "@etc/acknowledgements.json";
import { useNavigate } from "react-router-dom";
import { useQuery, isValidLanguageValue } from "@app/utils";
import { useTokenizedLinks } from "@app/hooks";
import { LinkSourceData } from "@app/hooks/useTokenizedLinks";
import { routes } from "@app/routing";
import {
  GuestValidationInput,
  Channel,
  PreBillErrorResponse,
  PRE_BILL_ERROR_CODES,
  ClosingStatus,
} from "@app/preBill/models";
import { PreBillAPIContext } from "@app/preBill/provider";
import DOBValidationErrorNotification from "@app/preBill/components/DOBValidationErrorNotification/dobValidationErrorNotification";
import { SpinnerView } from "@app/components";
import UPSDobVerificationFailed from "@app/ups/pages/private/upsDobVerificationFailed";

import "./../../../preBill.scss";

const messages = defineMessages({
  guestLoginPageTitle: {
    id: "guestLoginPageTitle",
    defaultMessage: "Review your billing update",
  },
  guestLoginPageSubtitle: {
    id: "guestLoginPageSubtitle",
    defaultMessage: "Please enter your date of birth.",
  },
  guestLoginDateOfBirthVerificationIssues: {
    id: "guestLoginDateOfBirthVerificationIssues",
    defaultMessage: "Need help? {contactUs}",
  },
  contactUsText: {
    id: "contactUsText",
    defaultMessage: "Contact Us",
  },
  guestLoginCookies: {
    id: "guestLoginCookies",
    defaultMessage:
      "This site uses cookies to enhance your experience. Any cookies preferences selected on Natera.com will not apply.",
  },
  guestLoginAcknowledgements: {
    id: "guestLoginAcknowledgements",
    defaultMessage:
      "By continuing you accept our {termsOfUse}, {privacyPolicy} and {privacyPractices}",
  },
  guestLoginTermsOfUse: {
    id: "guestLoginTermsOfUse",
    defaultMessage: "Terms of Use",
  },
  guestLoginPrivacyPolicy: {
    id: "guestLoginPrivacyPolicy",
    defaultMessage: "Privacy Policy",
  },
  guestLoginPrivacyPractices: {
    id: "guestLoginPrivacyPractices",
    defaultMessage: "Notice of Privacy Practices",
  },
});

const GuestLogin: FC = () => {
  const intl = useIntl();
  const errorController = useErrorController();
  const navigate = useNavigate();
  const { changeLanguage, currentLanguage } = useContext(IntlContext);
  const { orderUidPublic, setOrderUidPublic, setNotificationType } =
    useContext(PreBillContext);
  const { guestValidation, getOrderEstimate } = useContext(PreBillAPIContext);
  const { profile } = useContext(UppAuthContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [spinnerLoading, setSpinnerLoading] = useState<boolean>(false);
  const [showDOBValidationBoxError, setShowDOBValidationBoxError] =
    React.useState<boolean>(false);
  const ATTEMPTS = 3;
  const [attemptsLeft, setAttemptsLeft] = React.useState<number>(ATTEMPTS);
  const query = useQuery();
  const token = query.get("token");
  const { getOrderUidPublic } = useTokenizedLinks();

  const redirectionMap: Record<string, string> = {
    [Channel.INN]: routes.billingEstimate,
    [Channel.NCS]: routes.nonCoveredService,
    [Channel.NCS_V2]: routes.nonCoveredService,
    [Channel.OON]: routes.outOfNetwork,
    [Channel.PTPA]: routes.priorAuthorization,
  };

  const getOrderUidFromToken = async (token: string) => {
    const payload = await getOrderUidPublic(token);
    if (payload) {
      setOrderUidPublic(payload.orderUidPublic);
      setUserLanguage(payload.sourceData);
    }
  };

  const setUserLanguage = async (source: LinkSourceData) => {
    const language = source?.language;
    const notificationType = source?.notificationType;

    if (notificationType) {
      setNotificationType(notificationType);
    }

    if (
      language &&
      currentLanguage !== language &&
      isValidLanguageValue(language)
    ) {
      changeLanguage(language);
    }
  };

  const handleRedirection = (channel: string) => {
    const path = redirectionMap[channel] || routes.guestLogin;
    navigate(token ? `${path}?token=${token}` : path);
  };

  const handleGuestValidation = async (data: GuestValidationInput) => {
    setLoading(true);
    try {
      await guestValidation(data);
      await handleOrderEstimate();
    } catch (error) {
      handleGuestValidationError(error);
    } finally {
      setLoading(false);
    }
  };

  const navigateToClosingStatus = (closingStatus?: ClosingStatus) => {
    const path =
      closingStatus === ClosingStatus.INSURANCE
        ? routes.insuranceNoLongerAvailable
        : routes.noLongerAvailable;
    navigate(token ? `${path}?token=${token}` : path);
  };

  const handleGuestValidationError = (error: PreBillErrorResponse) => {
    if (error.code === PRE_BILL_ERROR_CODES.DOB_VALIDATION) {
      setAttemptsLeft((prev) => {
        const next = prev - 1;
        if (next > 0) {
          setShowDOBValidationBoxError(true);
        } else {
          setShowDOBValidationBoxError(false);
        }
        return next;
      });
    } else if (error.code === PRE_BILL_ERROR_CODES.ORDER_NOT_EXPIRED) {
      navigateToClosingStatus(error.closingStatus);
    }
  };

  const handleOrderEstimate = async () => {
    setSpinnerLoading(true);
    try {
      const response = await getOrderEstimate();
      handleRedirection(response.channel);
    } catch (error) {
      if (error.code === PRE_BILL_ERROR_CODES.ORDER_NOT_EXPIRED) {
        navigateToClosingStatus(error.closingStatus);
      }
    } finally {
      setSpinnerLoading(false);
    }
  };

  const handleDateOfBirthSubmit = (date: GuestValidationInput) => {
    handleGuestValidation(date);
  };

  useEffect(() => {
    if (!token) {
      navigate(routes.home, { replace: true });
      return;
    }
    getOrderUidFromToken(token);
  }, [token]);

  useEffect(() => {
    if (orderUidPublic && profile) {
      handleOrderEstimate();
    }
  }, [orderUidPublic, profile]);

  const title: ReactElement = (
    <section className="prebill-guest-login-page__container__title">
      <h2>{intl.formatMessage(messages.guestLoginPageTitle)}</h2>
      <span>{intl.formatMessage(messages.guestLoginPageSubtitle)}</span>
    </section>
  );

  if (attemptsLeft <= 0) {
    return (
      <UPSDobVerificationFailed
        onCreateAccount={() => navigate(routes.signUp)}
      />
    );
  }

  return (
    <div className="prebill-guest-login-page billing-gradient">
      {profile ? (
        <SpinnerView isLoading={spinnerLoading} />
      ) : (
        <div className="prebill-guest-login-page__container">
          {title}
          {showDOBValidationBoxError && <DOBValidationErrorNotification />}
          <ErrorProvider controller={errorController}>
            <DateOfBirthVerification
              loading={loading}
              onSubmit={handleDateOfBirthSubmit}
            />
          </ErrorProvider>
          <p className="prebill-guest-login-page__container__bottom-text">
            {intl.formatMessage(
              messages.guestLoginDateOfBirthVerificationIssues,
              {
                contactUs: (
                  <Link
                    target="_blank"
                    key="privacyPolicy"
                    rel="noreferrer"
                    to={acknowledgements.links.contactUs}
                  >
                    {intl.formatMessage(messages.contactUsText)}
                  </Link>
                ),
              }
            )}
          </p>
          <div className="prebill-guest-login-page__container__footer">
            <p className="prebill-guest-login-page__container__footer-cookies">
              {intl.formatMessage(messages.guestLoginCookies)}
            </p>
            <p className="prebill-guest-login-page__container__footer-terms">
              {intl.formatMessage(messages.guestLoginAcknowledgements, {
                termsOfUse: (
                  <a
                    href={acknowledgements.links.termsOfUse}
                    key="termsOfUse"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {intl.formatMessage(messages.guestLoginTermsOfUse)}
                  </a>
                ),
                privacyPolicy: (
                  <a
                    href={acknowledgements.links.privacyPolicy}
                    key="privacyPolicy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {intl.formatMessage(messages.guestLoginPrivacyPolicy)}
                  </a>
                ),
                privacyPractices: (
                  <a
                    href={acknowledgements.links.noticeOfPrivacyPractices}
                    key="privacyPractices"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {intl.formatMessage(messages.guestLoginPrivacyPractices)}
                  </a>
                ),
              })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestLogin;
